import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import '../../../App-global.css'
import './Editor.css'
import Sidebar from '../../../shared/components/layout/Sidebar'
import VerticalNav from '../../../shared/components/layout/VerticalNav'
import Timeline from '../components/Timeline'
import AIScriptWriter from '../../ai-script/components/ScriptWriter'
import ScriptSidebar from '../../ai-script/components/ScriptSidebar'
import AIImageGenerator from '../../ai-image/components/ImageGenerator'
import ImageSidebar from '../../ai-image/components/ImageSidebar'
import StockImages from '../../stock-images/components/StockImages'
import StockSidebar from '../../stock-images/components/StockSidebar'
import AnalyticsDashboard from '../../youtube-analytics/components/AnalyticsDashboard'
import AnalyticsSidebar from '../../youtube-analytics/components/AnalyticsSidebar'
import BenchmarkAnalysis from '../../benchmark-analysis/components/BenchmarkAnalysis'
import BenchmarkSidebar from '../../benchmark-analysis/components/BenchmarkSidebar'
import PreviewButton from '../components/PreviewButton'
import FinalPreview from '../components/FinalPreview'
import LoadingOverlay from '../../../shared/components/ui/LoadingOverlay'
import useSections from '../../../shared/hooks/useSections.js'
import useScriptParser from '../../../shared/hooks/useScriptParser.js'
import useVideoGeneration from '../../../shared/hooks/useVideoGeneration.js'
import { DEFAULT_SUBTITLE_SETTINGS, DEFAULT_TITLE_SETTINGS, DEFAULT_VIDEO_SETTINGS } from '../../../shared/services/constants'
import { useAuth } from '../../../shared/hooks/useAuth.js'
import { prepareSectionsForIndexedDB, restoreSectionsFromIndexedDB, deleteProjectFiles } from '../../../shared/utils/indexedDBUtils'
import logger from '../../../shared/utils/logger'
import { API_BASE_URL } from '../../../shared/utils/config'

function Editor() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { user, getAuthHeaders } = useAuth()
  // API_BASE_URL은 config에서 자동으로 가져옴 (개발/운영 자동 선택)
  // 상태 관리
  const [selectedVoice, setSelectedVoice] = useState('female')
  const [editingSection, setEditingSection] = useState(null)
  const [titleSettings, setTitleSettings] = useState(DEFAULT_TITLE_SETTINGS)
  const [subtitleSettings, setSubtitleSettings] = useState(DEFAULT_SUBTITLE_SETTINGS)
  const [videoSettings, setVideoSettings] = useState(DEFAULT_VIDEO_SETTINGS)
  const [saveStatus, setSaveStatus] = useState('saved') // 'saved', 'saving', 'error'
  const [lastSaved, setLastSaved] = useState(null)
  const [activeTab, setActiveTab] = useState('video') // 'script', 'image', 'video' - 기본값을 video로 변경
  const [isSidebarOpen, setIsSidebarOpen] = useState(true) // 사이드바 토글 상태
  const [isLoadingProject, setIsLoadingProject] = useState(false) // 프로젝트 로딩 상태
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true) // 자동 저장 활성화
  const [isPreviewOpen, setIsPreviewOpen] = useState(false) // 미리보기 상태
  const [ttsProgress, setTtsProgress] = useState(0) // TTS 생성 진행률
  const [currentTtsSection, setCurrentTtsSection] = useState(null) // 현재 처리 중인 섹션
  const [ttsLoadingMessage, setTtsLoadingMessage] = useState('') // TTS 로딩 메시지
  const [isUploadingAudio, setIsUploadingAudio] = useState(false) // 음성 업로드 상태
  const [uploadingMediaSections, setUploadingMediaSections] = useState({}) // 각 섹션별 미디어 업로드 상태
  
  // AI 이미지 탭 설정
  const [imageSettings, setImageSettings] = useState({
    style: 'realistic',
    size: '512x512',
    quality: 7,
    count: 1,
    negativePrompt: '',
    seed: ''
  })
  
  // AI 대본 탭 설정
  const [scriptSettings, setScriptSettings] = useState({
    topic: '',
    style: 'informative',
    length: 'shorts',
    audience: 'general',
    tone: 5,
    keywords: '',
    sectionCount: 5,
    includeCTA: false,
    includeHook: true
  })
  
  // 유튜브 채널 분석 설정
  const [analyticsSettings, setAnalyticsSettings] = useState({
    isConnected: false,
    channelName: '',
    defaultPeriod: '7days',
    exportFormat: 'csv',
    autoReport: false,
    reportEmail: '',
    showSubscribers: true,
    showViews: true,
    showVideos: true,
    showLikes: true,
    showComments: true,
    showCTR: true,
    showRetention: false,
    showRevenue: false,
    showDemographics: false
  })
  
  // 벤치마킹 분석 설정
  const [benchmarkSettings, setBenchmarkSettings] = useState({
    analysisDepth: 'basic',
    analysisPeriod: '30days',
    videoLimit: 10,
    compareViews: true,
    compareEngagement: true,
    compareTags: true,
    compareThumbnails: true,
    compareUploadTime: true,
    analyzeSERP: true,
    analyzeBacklinks: false,
    analyzeKeywordDifficulty: true,
    autoRecommend: true,
    apiConnected: false,
    apiKey: '',
    apiEndpoint: 'live',
    rateLimit: 'moderate',
    enableCache: true,
    cacheDuration: '1hour'
  })
  
  // 무료 스톡 설정
  const [stockSettings, setStockSettings] = useState({
    size: 'large',
    orientation: 'all',
    perPage: 30,
    color: null,
    category: null
  })
  
  const [savedStockImages, setSavedStockImages] = useState([])

  // 커스텀 훅 사용 - 프로젝트명과 영상 제목 분리
  const [projectName, setProjectName] = useState('') // 프로젝트명
  const { title, setTitle, script, setScript, parseScript } = useScriptParser() // title은 영상 제목
  const { 
    sections, 
    setSections,
    addSections, 
    updateSectionFiles, 
    updateSectionText, 
    removeSection,
    removeMediaOnly, 
    getSectionById 
  } = useSections()
  
  // YouTube 비디오 선택 메시지 수신
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'SELECT_YOUTUBE_VIDEO') {
        // YouTube 탭에서 비디오를 선택한 경우
        localStorage.setItem('selectedYouTubeVideo', JSON.stringify(event.data.video));
        setActiveTab('benchmark'); // 벤치마킹 탭으로 전환
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [])
  
  // URL 파라미터에서 tab 확인 (벤치마킹 탭 직접 이동 지원)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    const videoId = urlParams.get('videoId');
    
    if (tab === 'benchmark') {
      setActiveTab('benchmark');
      // URL에서 파라미터 제거 (깔끔한 URL 유지)
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // lastProjectId 저장 (YouTube 페이지에서 사용)
    if (projectId && projectId !== 'new') {
      localStorage.setItem('lastProjectId', projectId);
    }
  }, [projectId])
  const {
    isGeneratingTTS,
    isConvertingMp4,
    isGeneratingAllTTS,
    isGeneratingAllVideos,
    isMergingVideos,
    mergeProgress,
    mergeStatus,
    handleGenerateTTS,
    handleGenerateVideoWithTTS,
    handleConvertImageToMp4,
    handleAddSubtitleToVideo,
    handleGenerateAllTTS,
    handleGenerateAllVideos,
    handleMergeSelectedVideos
  } = useVideoGeneration(projectId) // projectId 전달

  // 이벤트 핸들러들
  const handleParseScript = () => {
    parseScript(addSections, setTitleSettings)
  }

  const handleSectionTextUpdate = async (sectionId, newText) => {
    // 텍스트만 업데이트하고 비디오 변환은 하지 않음
    updateSectionText(sectionId, newText)
    logger.debug('📝 대본이 수정되었습니다.')
    
    // 자막 미리보기를 위한 상태 업데이트만 수행
    // 실제 MP4 변환은 사용자가 별도로 요청할 때만 수행
  }

  const handleFileUpload = async (sectionId, file) => {
    // 파일 확장자 체크
    const fileName = file.name.toLowerCase()
    const isVideoByExt = fileName.endsWith('.mp4') || fileName.endsWith('.webm') || 
                        fileName.endsWith('.avi') || fileName.endsWith('.mov')
    const isImageByExt = fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || 
                        fileName.endsWith('.png') || fileName.endsWith('.gif') || 
                        fileName.endsWith('.webp')
    
    // 업로드 시작 시 로딩 표시 (각 섹션별로)
    setUploadingMediaSections(prev => ({ ...prev, [sectionId]: true }))
    
    try {
      logger.debug('🚀 handleFileUpload 시작:', { 
        sectionId, 
        fileName: file.name, 
        fileType: file.type || 'unknown',
        fileSize: file.size,
        isVideo: file.type.startsWith('video/') || isVideoByExt,
      isImage: file.type.startsWith('image/') || isImageByExt,
      extension: fileName.split('.').pop()
    })
    
    const currentSection = getSectionById(sectionId)
    logger.debug('📍 현재 섹션 상태:', currentSection)
    
    // 파일 타입에 따른 처리 (MIME 타입 또는 확장자로 판단)
    if (file.type.startsWith('image/') || isImageByExt) {
      logger.debug('🖼️ 이미지 파일 처리 시작')
      
      let imageUrl = null
      
      // 프로젝트 ID가 있으면 Supabase에 업로드
      if (projectId && projectId !== 'new') {
        try {
          logger.debug('🎯 Supabase Storage에 이미지 업로드 중...')
          const { uploadProjectMedia } = await import('../../../shared/services/api')
          // 프로젝트명도 함께 전달 (저장 경로용)
          imageUrl = await uploadProjectMedia(projectId, sectionId, file, title || 'untitled')
          logger.debug('✅ Supabase 이미지 업로드 성공:', imageUrl)
        } catch (error) {
          console.error('Supabase 업로드 실패, 로컬 파일 사용:', error)
          // 실패 시 로컬 파일 사용
        }
      }
      
      // 이미지 파일 처리 (비디오 관련 데이터 제거)
      updateSectionFiles(sectionId, {
        image: file,
        imageUrl: imageUrl,  // Supabase URL 저장
        video: null,  // 이미지 업로드 시 비디오 제거
        url: '',  // URL 초기화
        audio: currentSection?.audioFile,
        mp4: null  // MP4도 제거
      })
      
      logger.debug('📸 이미지 파일 상태 업데이트 완료')
      
      // MP4 변환 제거 - Canvas에서 직접 렌더링
      logger.debug('🎨 Canvas에서 이미지 직접 렌더링 (서버 변환 없음)')
    } else if (file.type.startsWith('video/') || isVideoByExt) {
      logger.debug('🎥 비디오 파일 처리 시작')
      logger.debug('비디오 파일 정보:', {
        name: file.name,
        type: file.type,
        size: file.size
      })
      
      let videoUrl = URL.createObjectURL(file)
      let supabaseUrl = null
      
      // 프로젝트 ID가 있으면 Supabase에 업로드
      if (projectId && projectId !== 'new') {
        try {
          logger.debug('🎯 Supabase Storage에 비디오 업로드 중...')
          const { uploadProjectMedia } = await import('../../../shared/services/api')
          // 프로젝트명도 함께 전달 (저장 경로용)
          supabaseUrl = await uploadProjectMedia(projectId, sectionId, file, title || 'untitled')
          logger.debug('✅ Supabase 비디오 업로드 성공:', supabaseUrl)
        } catch (error) {
          console.error('Supabase 업로드 실패, 로컬 파일 사용:', error)
          // 실패 시 로컬 파일 사용
        }
      }
      
      logger.debug('📹 Blob URL 생성:', videoUrl)
      
      const updateData = {
        image: null,  // 비디오 업로드 시 이미지 제거
        video: file,  // 원본 비디오 파일 객체
        videoUrl: supabaseUrl,  // Supabase 비디오 URL (백엔드용)
        audio: currentSection?.audioFile,
        mp4: videoUrl,  // 로컬 Blob URL (미리보기용)
        hasAudioInVideo: true  // 비디오에 오디오 포함 가정
      }
      
      logger.debug('🔄 업데이트할 데이터:', updateData)
      updateSectionFiles(sectionId, updateData)
      
      logger.debug('✅ 비디오 파일 업로드 완료')
    } else {
      console.error('❌ 지원하지 않는 파일 형식:', file.type)
      // alert 제거 - 콘솔에만 오류 표시
    }
    } finally {
      // 로딩 상태 해제
      setUploadingMediaSections(prev => {
        const newState = { ...prev }
        delete newState[sectionId]
        return newState
      })
    }
  }

  const handleFileDrop = async (e, sectionId, fileType) => {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    
    logger.debug(`🎯 개별 섹션 ${sectionId}에 ${files.length}개 파일 드롭`)
    
    for (const file of files) {
      // 이미지나 비디오 모두 handleFileUpload로 처리
      if (file.type.startsWith('image/') || file.type.startsWith('video/') || 
          file.name.match(/\.(jpg|jpeg|png|gif|webp|mp4|mov|avi|webm)$/i)) {
        logger.debug(`📥 섹션 ${sectionId}에 파일 처리: ${file.name}`)
        await handleFileUpload(sectionId, file)
      } else {
        logger.debug(`⚠️ 지원하지 않는 파일 형식: ${file.name} (${file.type})`)
      }
    }
  }

  const handleTTSGeneration = async (sectionId, text) => {
    logger.debug(`🎯 TTS 생성 요청 - Section ID: ${sectionId}, Text: ${text?.substring(0, 30)}...`)
    
    // 개별 TTS 생성 시 로딩 표시
    const section = getSectionById(sectionId)
    setCurrentTtsSection(section?.index || sectionId)
    setTtsLoadingMessage(`섹션 ${section?.index || ''}의 음성을 생성하고 있습니다...`)
    setTtsProgress(50) // 진행중임을 표시
    
    try {
      await handleGenerateTTS(
        sectionId, 
        text, 
        selectedVoice, 
        updateSectionFiles, 
        getSectionById
      )
      // TTS 생성 후 전체 섹션 상태 확인
      logger.debug('📋 TTS 생성 후 전체 섹션 상태:', sections.map(s => ({
        id: s.id,
        text: s.text?.substring(0, 20) + '...',
        audioFile: s.audioFile
      })))
    } finally {
      // 로딩 상태 초기화
      setCurrentTtsSection(null)
      setTtsLoadingMessage('')
      setTtsProgress(0)
    }
  }

  const handleAllTTSGeneration = async () => {
    try {
      // 전체 TTS 생성 시 로딩 표시
      setTtsLoadingMessage('전체 음성을 생성하고 있습니다...')
      setTtsProgress(0)
      
      // 각 섹션별로 진행률 업데이트
      const totalSections = sections.length
      let completedSections = 0
      
      // handleGenerateAllTTS 호출 전에 진행률 콜백 설정
      const originalHandleGenerateAllTTS = handleGenerateAllTTS
      
      // 각 섹션 처리 시마다 진행률 업데이트 (실제로는 handleGenerateAllTTS 내부에서 처리)
      await handleGenerateAllTTS(sections, selectedVoice, (sectionId, sectionFiles) => {
        completedSections++
        const progress = Math.round((completedSections / totalSections) * 100)
        setTtsProgress(progress)
        const section = sections.find(s => s.id === sectionId)
        setCurrentTtsSection(section?.index)
        setTtsLoadingMessage(`음성 생성 중... (${completedSections}/${totalSections})`)
        updateSectionFiles(sectionId, sectionFiles)
      })
      
    } catch (error) {
      logger.error('전체 음성 생성 실패:', error)
    } finally {
      // 로딩 상태 초기화
      setCurrentTtsSection(null)
      setTtsLoadingMessage('')
      setTtsProgress(0)
    }
  }

  const handleAllVideosGeneration = async () => {
    
    logger.debug('📤 Export 모드: 전체 영상 생성 시작')
    await handleGenerateAllVideos(sections, selectedVoice, subtitleSettings, null, updateSectionFiles, getSectionById)
    
    // 완료 후 원래 모드로 복원
    setIsPreviewMode(originalPreviewMode)
  }

  // 대량 미디어(이미지/비디오) 업로드 처리
  const handleBulkImageUpload = async (files) => {
    logger.debug(`📁 ${files.length}개 미디어 파일 업로드 시작`)
    
    // 파일 타입 구분
    const imageFiles = files.filter(f => 
      f.type.startsWith('image/') || 
      f.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)
    )
    const videoFiles = files.filter(f => 
      f.type.startsWith('video/') || 
      f.name.match(/\.(mp4|mov|avi|webm)$/i)
    )
    
    logger.debug(`📷 이미지: ${imageFiles.length}개, 🎥 비디오: ${videoFiles.length}개`)
    
    // 미디어가 없는 섹션들만 필터링
    const sectionsWithoutMedia = sections.filter(section => !section.imageFile && !section.videoFile)
    
    if (sectionsWithoutMedia.length === 0) {
      alert('모든 섹션에 미디어가 이미 업로드되어 있습니다.')
      return
    }
    
    // 업로드할 파일 수와 섹션 수 맞추기
    const filesToUpload = files.slice(0, sectionsWithoutMedia.length)
    const sectionsToUpdate = sectionsWithoutMedia.slice(0, files.length)
    
    logger.debug(`🎯 ${sectionsToUpdate.length}개 섹션에 미디어를 자동 배치합니다`)
    
    // 병렬로 모든 이미지 업로드 및 변환
    const uploadPromises = sectionsToUpdate.map(async (section, index) => {
      const file = filesToUpload[index]
      try {
        const fileType = file.type.startsWith('video/') ? '🎥 비디오' : '📷 이미지'
        logger.debug(`${fileType} 섹션 ${section.index}에 업로드: ${file.name}`)
        await handleFileUpload(section.id, file)
        return { success: true, sectionId: section.id, fileName: file.name }
      } catch (error) {
        console.error(`❌ 섹션 ${section.id} 미디어 업로드 실패:`, error)
        return { success: false, sectionId: section.id, fileName: file.name, error }
      }
    })
    
    // 모든 업로드 작업 병렬 실행
    const results = await Promise.allSettled(uploadPromises)
    const successful = results.filter(result => 
      result.status === 'fulfilled' && result.value.success
    ).length
    const failed = results.filter(result => 
      result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)
    ).length
    
    logger.debug(`✅ 대량 미디어 업로드 완료: 성공 ${successful}개, 실패 ${failed}개`)
    
    // 업로드 결과를 콘솔에만 표시
    if (failed > 0) {
      logger.debug(`📁 미디어 업로드 완료 - 성공: ${successful}개, 실패: ${failed}개`)
    } else {
      logger.debug(`✅ 미디어 업로드 완료! (${successful}개 파일)`)
    }
  }

  // 개별 음성 업로드 처리
  const handleAudioUpload = async (sectionId, audioFile) => {
    const currentSection = getSectionById(sectionId)
    
    // 음성 업로드 시 로딩 표시
    if (audioFile) {
      setIsUploadingAudio(true)
      setTtsLoadingMessage(`섹션 ${currentSection?.index || ''}에 음성 업로드 중...`)
      setTtsProgress(50)
    }
    
    try {
      if (!audioFile) {
      // 음성 삭제
      updateSectionFiles(sectionId, {
        image: currentSection?.imageFile,
        video: currentSection?.videoFile,
        url: currentSection?.urlMedia,
        audio: null,
        mp4: currentSection?.mp4File,
        hasAudioInVideo: false
      })
      logger.debug(`🗑️ 섹션 ${currentSection?.index} 음성 삭제됨`)
      return
    }
    
    // 음성 파일을 Blob URL로 변환 (재생용)
    const audioUrl = URL.createObjectURL(audioFile)
    
    // 섹션 파일 업데이트 (audioFile 객체도 저장)
    updateSectionFiles(sectionId, {
      image: currentSection?.imageFile,
      video: currentSection?.videoFile,
      url: currentSection?.urlMedia,
      audio: audioUrl,
      audioFileObject: audioFile, // 실제 파일 객체도 저장
      mp4: currentSection?.mp4File,
      hasAudioInVideo: true
    })
    
    logger.debug(`🎵 섹션 ${currentSection?.index}에 음성 업로드: ${audioFile.name}`)
    
    // 이미지가 있으면 음성 포함 비디오 자동 재생성
    if (currentSection?.imageFile) {
      logger.debug('🎬 음성 업로드 완료, 비디오를 재생성합니다.')
      
      // 이미지 파일을 Blob에서 File 객체로 변환 (필요한 경우)
      let imageFile = currentSection.imageFile
      if (typeof imageFile === 'string' && imageFile.startsWith('blob:')) {
        // Blob URL인 경우 fetch로 가져와서 File 객체로 변환
        const response = await fetch(imageFile)
        const blob = await response.blob()
        imageFile = new File([blob], 'image.jpg', { type: blob.type })
      }
      
      // 업로드된 오디오와 함께 비디오 생성
      const { generateVideoWithUploadedAudio } = await import('../../../shared/services/api')
      const mp4Url = await generateVideoWithUploadedAudio(
        imageFile,
        audioFile,
        currentSection.text,
        subtitleSettings,
        null,  // videoSettings 제거 - Canvas가 실시간 조정
        titleSettings
      )
      
      // MP4 파일 업데이트
      updateSectionFiles(sectionId, {
        image: currentSection.imageFile,
        video: currentSection.videoFile,
        url: currentSection.urlMedia,
        audio: audioUrl,
        audioFileObject: audioFile,
        mp4: mp4Url,
        hasAudioInVideo: true
      })
    }
    } finally {
      // 로딩 상태 초기화
      if (audioFile) {
        setIsUploadingAudio(false)
        setTtsLoadingMessage('')
        setTtsProgress(0)
      }
    }
  }

  // 대량 음성 업로드 처리
  const handleBulkAudioUpload = async (files) => {
    logger.debug(`🎵 ${files.length}개 음성 파일 업로드 시작`)
    
    // 음성이 없는 섹션들만 필터링
    const sectionsWithoutAudio = sections.filter(section => !section.audioFile)
    
    if (sectionsWithoutAudio.length === 0) {
      alert('모든 섹션에 음성이 이미 업로드되어 있습니다.')
      return
    }
    
    // 업로드할 파일 수와 섹션 수 맞추기
    const filesToUpload = files.slice(0, sectionsWithoutAudio.length)
    const sectionsToUpdate = sectionsWithoutAudio.slice(0, files.length)
    
    logger.debug(`🎯 ${sectionsToUpdate.length}개 섹션에 음성을 자동 배치합니다`)
    
    // 병렬로 모든 음성 업로드
    const uploadPromises = sectionsToUpdate.map(async (section, index) => {
      const file = filesToUpload[index]
      try {
        logger.debug(`🎵 섹션 ${section.index}에 음성 업로드: ${file.name}`)
        await handleAudioUpload(section.id, file)
        return { success: true, sectionId: section.id, fileName: file.name }
      } catch (error) {
        console.error(`❌ 섹션 ${section.id} 음성 업로드 실패:`, error)
        return { success: false, sectionId: section.id, fileName: file.name, error }
      }
    })
    
    // 모든 업로드 작업 병렬 실행
    const results = await Promise.allSettled(uploadPromises)
    const successful = results.filter(result => 
      result.status === 'fulfilled' && result.value.success
    ).length
    const failed = results.filter(result => 
      result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)
    ).length
    
    logger.debug(`✅ 대량 음성 업로드 완료: 성공 ${successful}개, 실패 ${failed}개`)
    
    if (failed > 0) {
      alert(`음성 업로드가 완료되었습니다!\n성공: ${successful}개, 실패: ${failed}개`)
    } else {
      alert(`음성 업로드가 완료되었습니다! (${successful}개 음성)`)
    }
  }

  // 자막 설정 실시간 변경 처리 (Canvas 미리보기만 사용)
  const handleSubtitleSettingsChange = async (newSettings) => {
    // 🎨 Canvas 미리보기가 자동으로 실시간 업데이트됨
    // 서버 요청 없이 Canvas만 사용
    logger.debug('🎨 Canvas 자막 설정 변경 (서버 요청 없음):', newSettings)
    
    // 설정만 업데이트하면 Canvas가 자동으로 다시 렌더링
    // CanvasSubtitlePreview 컴포넌트가 settings를 dependency로 가지고 있어서
    // 자동으로 useEffect가 실행되어 다시 그려짐
  }

  // 비디오 설정 실시간 변경 처리 (Canvas 미리보기만)
  // 제목 설정 실시간 변경 처리 (Canvas 미리보기만)
  const handleTitleSettingsChange = async (newSettings) => {
    // 🎨 Canvas 미리보기가 자동으로 실시간 업데이트됨
    logger.debug('🎨 Canvas 제목 설정 변경 (서버 요청 없음):', newSettings)
    // 설정만 업데이트하면 Canvas가 자동으로 다시 렌더링
    // CanvasSubtitlePreview 컴포넌트가 titleSettings를 dependency로 가지고 있어서
    // 자동으로 useEffect가 실행되어 다시 그려짐
  }

  const handleVideoSettingsChange = async (newSettings) => {
    // 🎬 Canvas 미리보기가 자동으로 실시간 업데이트됨 
    // 서버 요청 없이 Canvas만 사용
    logger.debug('🎬 Canvas 비디오 설정 변경 (서버 요청 없음):', newSettings)
    // 비디오 설정은 다운로드 시에만 서버에 적용됨
  }

  // 백엔드에서 프로젝트 데이터 로드
  useEffect(() => {
    const loadProject = async () => {
      if (projectId && projectId !== 'new' && user) {
        setIsLoadingProject(true)
        try {
          // 백엔드 API에서 프로젝트 로드
          const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
            headers: getAuthHeaders()
          })
          
          if (response.ok) {
            const projectData = await response.json()
            const project = projectData.project || projectData
            
            // 프로젝트 기본 정보 설정
            setProjectName(project.name || '') // 프로젝트명 설정
            setTitle(project.title || '') // 영상 제목 설정 (별도 저장된 값만 사용)
            
            // settings에서 데이터 복원
            if (project.settings) {
              // 섹션 데이터 복원 (IndexedDB에서 파일 데이터 복원)
              if (project.settings.sections) {
                const restoredSections = await restoreSectionsFromIndexedDB(project.settings.sections)
                setSections(restoredSections)
              }
              
              // 설정 복원
              if (project.settings.titleSettings) {
                setTitleSettings(project.settings.titleSettings)
                // titleSettings.text가 있으면 영상 제목으로 사용
                if (project.settings.titleSettings.text) {
                  setTitle(project.settings.titleSettings.text)
                }
              }
              if (project.settings.subtitleSettings) {
                setSubtitleSettings(project.settings.subtitleSettings)
              }
              if (project.settings.videoSettings) {
                setVideoSettings(project.settings.videoSettings)
              }
            }
          } else if (response.status === 404) {
            alert('프로젝트를 찾을 수 없습니다.')
            navigate('/')
          }
        } catch (error) {
          console.error('프로젝트 로드 실패:', error)
          alert('프로젝트를 불러오는 중 오류가 발생했습니다.')
        } finally {
          setIsLoadingProject(false)
        }
      }
    }
    
    loadProject()
  }, [projectId, user?.id]) // 의존성 최소화 - user.id만 체크

  // 스톡 캐시 정보 가져오기
  const getStockCacheInfo = () => {
    let count = 0
    let totalSize = 0
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('pexels_search_')) {
        count++
        const value = localStorage.getItem(key)
        if (value) {
          totalSize += value.length
        }
      }
    }
    
    const sizeKB = (totalSize / 1024).toFixed(2)
    return {
      count,
      size: `${sizeKB} KB`
    }
  }
  
  // 스톡 캐시 비우기
  const handleClearStockCache = () => {
    const keys = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('pexels_search_')) {
        keys.push(key)
      }
    }
    
    keys.forEach(key => localStorage.removeItem(key))
    alert('캐시가 모두 삭제되었습니다')
  }

  // 자동 저장 기능 (백엔드 API 사용)
  useEffect(() => {
    // sections.length === 0 조건 제거하여 프로젝트명만 변경해도 저장되도록 함
    if (!autoSaveEnabled || (!projectName && !title) || projectId === 'new' || !user) return
    
    // 디바운스를 위한 타이머 (3초)
    const saveTimer = setTimeout(async () => {
      setSaveStatus('saving')
      
      try {
        // IndexedDB에 파일 저장 (로컬)
        const preparedSections = await prepareSectionsForIndexedDB(projectId, sections)
        
        // 백엔드에 자동 저장
        const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: projectName || title, // 프로젝트명 우선 사용
            title: title, // 영상 제목 (독립적으로 저장)
            settings: {
              sections: preparedSections.map(s => {
                // 백엔드가 필요로 하는 필드 정리
                const cleanSection = {
                  id: s.id,
                  text: s.text,
                  index: s.index,
                  // 백엔드가 기대하는 URL 필드들
                  audioFile: typeof s.audioFile === 'string' ? s.audioFile : undefined,
                  imageUrl: s.imageUrl || undefined,  // Supabase 이미지 URL
                  videoUrl: s.videoUrl || undefined,  // Supabase 비디오 URL
                  // IndexedDB 관련 필드 (로컬 파일 복원용)
                  imageFileId: s.imageFileId,
                  videoFileId: s.videoFileId,
                  mp4FileId: s.mp4FileId
                }
                // undefined 값 제거
                return Object.fromEntries(
                  Object.entries(cleanSection).filter(([_, v]) => v !== undefined)
                )
              }),
              titleSettings,
              subtitleSettings,
              videoSettings,
              lastEditedAt: new Date().toISOString()
            }
          })
        })
        
        if (response.ok) {
          setSaveStatus('saved')
          setLastSaved(new Date())
          logger.debug('자동 저장 완료:', new Date().toLocaleTimeString())
        } else {
          setSaveStatus('error')
          console.error('자동 저장 실패')
        }
      } catch (error) {
        console.error('자동 저장 오류:', error)
        setSaveStatus('error')
      }
    }, 3000) // 3초 후 자동 저장

    return () => clearTimeout(saveTimer)
  }, [projectName, title, sections, projectId, user?.id, titleSettings, subtitleSettings, videoSettings, autoSaveEnabled]) // API_BASE_URL, getAuthHeaders 제거

  // 프로젝트 저장 (수동 - 새 프로젝트용)
  const saveProject = async () => {
    if (!projectName.trim() && !title.trim()) {
      alert('프로젝트명 또는 영상 제목을 입력해주세요')
      return
    }

    setSaveStatus('saving')
    
    try {
      // 새 프로젝트인 경우 먼저 생성
      let currentProjectId = projectId
      
      if (projectId === 'new') {
        // 백엔드에 새 프로젝트 생성
        const createResponse = await fetch(`${API_BASE_URL}/api/projects/create`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: projectName || title, // 프로젝트명 (없으면 영상 제목 사용)
            title: title, // 영상 제목 (별도 값)
            description: '비디오 편집 프로젝트',
            settings: {}
          })
        })
        
        if (createResponse.ok) {
          const data = await createResponse.json()
          currentProjectId = data.project.id
        } else {
          throw new Error('프로젝트 생성 실패')
        }
      }
      
      // IndexedDB에 파일 저장 (로컬)
      const preparedSections = await prepareSectionsForIndexedDB(currentProjectId, sections)
      
      // 백엔드에 프로젝트 데이터 업데이트
      const updateResponse = await fetch(`${API_BASE_URL}/api/projects/${currentProjectId}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: title,
          settings: {
            sections: preparedSections.map(s => {
              // 섹션 데이터 정리 - undefined 값 제거
              const cleanSection = {
                id: s.id,
                text: s.text,
                index: s.index,
                // URL 문자열은 유지, File 객체는 제외
                audioFile: typeof s.audioFile === 'string' ? s.audioFile : undefined,
                imageUrl: s.imageUrl || undefined,  // Supabase 이미지 URL
                videoUrl: s.videoUrl || undefined,  // Supabase 비디오 URL
                // IndexedDB 참조 ID들
                imageFileId: s.imageFileId,
                videoFileId: s.videoFileId,
                mp4FileId: s.mp4FileId,
                audioFileId: s.audioFileId
              }
              // undefined 값 제거
              return Object.fromEntries(
                Object.entries(cleanSection).filter(([_, v]) => v !== undefined)
              )
            }),
            titleSettings,
            subtitleSettings,
            videoSettings,
            lastEditedAt: new Date().toISOString()
          }
        })
      })
      
      if (updateResponse.ok) {
        setSaveStatus('saved')
        setLastSaved(new Date())
        alert('프로젝트가 저장되었습니다!')
        
        if (projectId === 'new') {
          navigate(`/editor/${currentProjectId}`)
        }
      } else {
        throw new Error('프로젝트 업데이트 실패')
      }
    } catch (error) {
      console.error('프로젝트 저장 실패:', error)
      setSaveStatus('error')
      alert('프로젝트 저장에 실패했습니다.')
    }
  }

  // 프로젝트 로딩 중일 때 표시
  if (isLoadingProject) {
    return (
      <div className="app" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#1a1a1a'
      }}>
        <div style={{ color: '#fff', textAlign: 'center' }}>
          <h2>프로젝트 로딩 중...</h2>
          <p style={{ color: '#999' }}>잠시만 기다려주세요</p>
        </div>
      </div>
    )
  }

  return (
    <div className="app">
      {/* 헤더 바 */}
      <div className={`editor-header ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate('/')} className="btn-back">
            프로젝트 목록
          </button>
        </div>
        <div className="header-center">
          <h2>{projectName || '새 프로젝트'}</h2>
        </div>
        {projectId === 'new' && (
          <button onClick={saveProject} className="btn-save-new">
            💾 프로젝트 저장
          </button>
        )}
      </div>
      
      {/* TTS 생성 및 음성 업로드 로딩 오버레이 */}
      {(Object.keys(isGeneratingTTS).some(key => isGeneratingTTS[key]) || isGeneratingAllTTS || isUploadingAudio) && (
        <LoadingOverlay
          isLoading={true}
          message={ttsLoadingMessage || '음성을 처리하고 있습니다...'}
          progress={ttsProgress}
          subMessage={currentTtsSection ? `섹션 ${currentTtsSection} 처리 중` : null}
        />
      )}
      
      {/* 영상 합치기 로딩 오버레이 */}
      {isMergingVideos && (
        <div className="loading-overlay">
          <div className="loading-modal">
            <div className="spinner-icon">⏳</div>
            <div className="loading-text">영상 합치는 중...</div>
            
            {/* 진행률 표시 */}
            <div style={{ marginTop: '20px', width: '100%' }}>
              <div style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                color: '#333',
                marginBottom: '10px'
              }}>
                {mergeProgress}%
              </div>
              
              {/* 진행 바 */}
              <div style={{
                width: '100%',
                height: '20px',
                backgroundColor: '#f0f0f0',
                borderRadius: '10px',
                overflow: 'hidden',
                marginBottom: '10px'
              }}>
                <div style={{
                  width: `${mergeProgress}%`,
                  height: '100%',
                  backgroundColor: '#667eea',
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '10px',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              
              {/* 상태 메시지 */}
              <div style={{ 
                fontSize: '14px', 
                color: '#666',
                minHeight: '20px'
              }}>
                {mergeStatus}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* 사이드바 - 영상 합치기 탭에서만 표시 */}
      {activeTab === 'video' && (
        <Sidebar 
          projectName={projectName}
          setProjectName={setProjectName}
          scriptTitle={title}
          setScriptTitle={setTitle}
          script={script}
          setScript={setScript}
          selectedVoice={selectedVoice}
          setSelectedVoice={setSelectedVoice}
          titleSettings={titleSettings}
          setTitleSettings={setTitleSettings}
          subtitleSettings={subtitleSettings}
          setSubtitleSettings={setSubtitleSettings}
          videoSettings={videoSettings}
          setVideoSettings={setVideoSettings}
          sections={sections}
          parseScript={handleParseScript}
          generateAllTTS={handleAllTTSGeneration}
          isGeneratingAllTTS={isGeneratingAllTTS}
          onTitleSettingsChange={handleTitleSettingsChange}
          onSubtitleSettingsChange={handleSubtitleSettingsChange}
          onVideoSettingsChange={handleVideoSettingsChange}
          onBulkImageUpload={handleBulkImageUpload}
          onBulkAudioUpload={handleBulkAudioUpload}
          onMergeVideos={handleMergeSelectedVideos}
          isOpen={isSidebarOpen}
        />
      )}
      
      {/* 벤치마킹 사이드바 - 벤치마킹 탭에서 표시 (완전 활성화) */}
      {activeTab === 'benchmark' && (
        <BenchmarkSidebar
          benchmarkSettings={benchmarkSettings}
          setBenchmarkSettings={setBenchmarkSettings}
          savedChannels={[]}
          onSaveChannel={(channel) => console.log('Save channel:', channel)}
          onRemoveChannel={(id) => console.log('Remove channel:', id)}
          isOpen={isSidebarOpen}
          youtubeUrl={benchmarkSettings.youtubeUrl || ''}
          onYoutubeUrlChange={(url) => setBenchmarkSettings(prev => ({ ...prev, youtubeUrl: url }))}
          onYoutubeUrlSubmit={() => {
            console.log('Sidebar submit button clicked');
            console.log('window.benchmarkHandleSubmit exists?', !!window.benchmarkHandleSubmit);
            if (window.benchmarkHandleSubmit) {
              window.benchmarkHandleSubmit();
            } else {
              console.error('benchmarkHandleSubmit is not defined on window');
            }
          }}
          selectedVideo={benchmarkSettings.selectedVideo}
          onDownloadVideo={async (video) => {
            // 즉시 로딩 표시
            const loadingDiv = document.createElement('div');
            loadingDiv.id = 'download-loading';
            loadingDiv.style.cssText = `
              position: fixed; 
              top: 0; left: 0; right: 0; bottom: 0; 
              background: rgba(0, 0, 0, 0.5); 
              display: flex; 
              align-items: center; 
              justify-content: center; 
              z-index: 9999;
            `;
            loadingDiv.innerHTML = `
              <div style="
                background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
                backdrop-filter: blur(10px);
                -webkit-backdrop-filter: blur(10px);
                border: 1px solid rgba(255, 255, 255, 0.2);
                padding: 40px 50px; 
                border-radius: 20px; 
                box-shadow: 0 20px 40px rgba(0,0,0,0.1), 0 10px 20px rgba(102, 126, 234, 0.2); 
                text-align: center;
                min-width: 320px;
                position: relative;
                overflow: hidden;
              ">
                <div style="
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.4));
                  border-radius: 20px;
                  z-index: -1;
                "></div>
                <div style="
                  width: 50px; 
                  height: 50px; 
                  border: 4px solid rgba(102, 126, 234, 0.2); 
                  border-top: 4px solid #667eea; 
                  border-radius: 50%; 
                  animation: spin 1s linear infinite; 
                  margin: 0 auto 20px;
                  filter: drop-shadow(0 4px 8px rgba(102, 126, 234, 0.3));
                "></div>
                <div style="
                  font-size: 18px; 
                  color: #2d3748; 
                  font-weight: 600;
                  margin-bottom: 8px;
                  text-shadow: 0 1px 2px rgba(0,0,0,0.1);
                ">영상 다운로드 준비 중</div>
                <div style="
                  font-size: 14px; 
                  color: #4a5568; 
                  opacity: 0.8;
                ">잠시만 기다려주세요 ✨</div>
              </div>
              <style>
                @keyframes spin { 
                  0% { transform: rotate(0deg); } 
                  100% { transform: rotate(360deg); } 
                }
              </style>
            `;
            document.body.appendChild(loadingDiv);

            try {
              // 기존 방식으로 다운로드 (로딩 표시 포함)
              const response = await fetch(`${API_BASE_URL}/api/ytdlp/download`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  url: video.url || `https://youtube.com/watch?v=${video.id}`,
                  quality: 'best'
                })
              });
              
              if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${video.title || video.id || 'video'}.mp4`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                
                // 로딩 제거
                if (loadingDiv && document.body.contains(loadingDiv)) {
                  document.body.removeChild(loadingDiv);
                }
              } else {
                const errorData = await response.json();
                throw new Error(errorData.error || '다운로드 실패');
              }
            } catch (error) {
              // 오류 시 로딩 제거
              const existingLoading = document.getElementById('download-loading') || document.getElementById('download-progress');
              if (existingLoading && document.body.contains(existingLoading)) {
                document.body.removeChild(existingLoading);
              }
              console.error('영상 다운로드 오류:', error);
              alert(`다운로드 실패: ${error.message}`);
            }
          }}
          onExtractAudio={async (video) => {
            // 즉시 로딩 표시
            const loadingDiv = document.createElement('div');
            loadingDiv.id = 'audio-loading';
            loadingDiv.style.cssText = `
              position: fixed; 
              top: 0; left: 0; right: 0; bottom: 0; 
              background: rgba(0, 0, 0, 0.5); 
              display: flex; 
              align-items: center; 
              justify-content: center; 
              z-index: 9999;
            `;
            loadingDiv.innerHTML = `
              <div style="
                background: linear-gradient(135deg, rgba(234, 88, 12, 0.1), rgba(251, 146, 60, 0.1));
                backdrop-filter: blur(10px);
                -webkit-backdrop-filter: blur(10px);
                border: 1px solid rgba(255, 255, 255, 0.2);
                padding: 40px 50px; 
                border-radius: 20px; 
                box-shadow: 0 20px 40px rgba(0,0,0,0.1), 0 10px 20px rgba(234, 88, 12, 0.2); 
                text-align: center;
                min-width: 320px;
                position: relative;
                overflow: hidden;
              ">
                <div style="
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.4));
                  border-radius: 20px;
                  z-index: -1;
                "></div>
                <div style="
                  width: 50px; 
                  height: 50px; 
                  border: 4px solid rgba(234, 88, 12, 0.2); 
                  border-top: 4px solid #ea580c; 
                  border-radius: 50%; 
                  animation: spin 1s linear infinite; 
                  margin: 0 auto 20px;
                  filter: drop-shadow(0 4px 8px rgba(234, 88, 12, 0.3));
                "></div>
                <div style="
                  font-size: 18px; 
                  color: #2d3748; 
                  font-weight: 600;
                  margin-bottom: 8px;
                  text-shadow: 0 1px 2px rgba(0,0,0,0.1);
                ">오디오 추출 중</div>
                <div style="
                  font-size: 14px; 
                  color: #4a5568; 
                  opacity: 0.8;
                ">잠시만 기다려주세요 🎵</div>
              </div>
              <style>
                @keyframes spin { 
                  0% { transform: rotate(0deg); } 
                  100% { transform: rotate(360deg); } 
                }
              </style>
            `;
            document.body.appendChild(loadingDiv);

            try {
              console.log('오디오 추출 요청 - video 객체:', video);
              const response = await fetch(`${API_BASE_URL}/api/ytdlp/extract-audio`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  url: video.url || `https://youtube.com/watch?v=${video.id}`,
                  extract_audio: true,
                  audio_format: 'mp3'
                })
              });
              
              if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${video.title || video.id || 'audio'}.mp3`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                
                // 로딩 제거
                if (loadingDiv && document.body.contains(loadingDiv)) {
                  document.body.removeChild(loadingDiv);
                }
              } else {
                const errorData = await response.json();
                throw new Error(errorData.error || '오디오 추출 실패');
              }
            } catch (error) {
              // 오류 시 로딩 제거
              if (loadingDiv && document.body.contains(loadingDiv)) {
                document.body.removeChild(loadingDiv);
              }
              console.error('오디오 추출 오류:', error);
              alert(`오디오 추출 실패: ${error.message}`);
            }
          }}
        />
      )}
      
      {/* AI 대본 사이드바 - 대본 탭에서만 표시 */}
      {activeTab === 'script' && import.meta.env.VITE_DEV_MODE === 'true' && (
        <ScriptSidebar
          scriptSettings={scriptSettings}
          setScriptSettings={setScriptSettings}
          onGenerateScript={() => console.log('Generate script with settings:', scriptSettings)}
          scriptTemplates={[]}
          recentTopics={[]}
          isOpen={isSidebarOpen}
        />
      )}
      
      {/* AI 이미지 사이드바 - 이미지 탭에서만 표시 */}
      {activeTab === 'image' && import.meta.env.VITE_DEV_MODE === 'true' && (
        <ImageSidebar
          imageSettings={imageSettings}
          setImageSettings={setImageSettings}
          onGenerateImage={() => console.log('Generate image with settings:', imageSettings)}
          recentPrompts={[]}
          favoriteStyles={[]}
          isOpen={isSidebarOpen}
        />
      )}
      
      {/* 무료 스톡 사이드바 - 스톡 탭에서만 표시 */}
      {activeTab === 'stock' && (
        <StockSidebar
          searchSettings={stockSettings}
          setSearchSettings={setStockSettings}
          savedCount={savedStockImages.length}
          onClearCache={handleClearStockCache}
          cacheInfo={getStockCacheInfo()}
        />
      )}
      
      {/* 유튜브 채널 분석 사이드바 - 분석 탭에서만 표시 */}
      {activeTab === 'analytics' && (
        <AnalyticsSidebar
          analyticsSettings={analyticsSettings}
          setAnalyticsSettings={setAnalyticsSettings}
          onConnectChannel={() => console.log('Connect YouTube channel')}
          onRefreshData={() => console.log('Refresh analytics data')}
          isOpen={isSidebarOpen}
        />
      )}
      
      {/* 세로 네비게이션 메뉴 (탭 역할) */}
      <VerticalNav 
        activeMenu={activeTab}
        onMenuChange={setActiveTab}
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      
      {/* 메인 레이아웃 */}
      <div className={`main-layout ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <div className="main-content">
          <div className="content-panel">
            {/* 벤치마킹 분석 탭 */}
            {activeTab === 'benchmark' && (
              <BenchmarkAnalysis 
                youtubeUrl={benchmarkSettings.youtubeUrl}
                onYoutubeUrlChange={(url) => setBenchmarkSettings(prev => ({ ...prev, youtubeUrl: url }))}
                onYoutubeUrlSubmit={true}  // Pass true to indicate the component should handle submit
                isLoadingMetadata={benchmarkSettings.isLoadingMetadata}
                onVideoSelect={(video) => setBenchmarkSettings(prev => ({ ...prev, selectedVideo: video }))}
              />
            )}
            
            {/* AI 대본 만들기 탭 - 개발 모드에서만 표시 */}
            {activeTab === 'script' && import.meta.env.VITE_DEV_MODE === 'true' && (
              <AIScriptWriter
                title={title}
                script={script}
                onTitleChange={setTitle}
                onScriptChange={setScript}
                onParseScript={parseScript}
              />
            )}
            
            {/* AI 이미지 생성 탭 - 개발 모드에서만 표시 */}
            {activeTab === 'image' && import.meta.env.VITE_DEV_MODE === 'true' && (
              <AIImageGenerator />
            )}
            
            {/* 무료 스톡 탭 - 개발 모드에서만 표시 */}
            {activeTab === 'stock' && (
              <StockImages />
            )}
            
            {/* 유튜브 채널 분석 탭 - 개발 모드에서만 표시 */}
            {activeTab === 'analytics' && (
              <AnalyticsDashboard />
            )}
            
            {/* YouTube 인기 동영상 탭 */}
            {activeTab === 'youtube' && (
              <div style={{ width: '100%', height: '100%' }}>
                <iframe
                  src="/youtube"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  title="YouTube Popular Videos"
                />
              </div>
            )}
            
            {/* 영상 편집 탭 */}
            {activeTab === 'video' && (
              <Timeline 
              sections={sections}
              editingSection={editingSection}
              setEditingSection={setEditingSection}
              isGeneratingTTS={isGeneratingTTS}
              isConvertingMp4={isConvertingMp4}
              uploadingMediaSections={uploadingMediaSections}
              selectedVoice={selectedVoice}
              subtitleSettings={subtitleSettings}
              titleSettings={titleSettings}
              videoSettings={videoSettings}
              isSidebarOpen={isSidebarOpen}
              onSectionTextUpdate={handleSectionTextUpdate}
              onFileUpload={handleFileUpload}
              onFileDrop={handleFileDrop}
              onRemoveSection={removeSection}
              onRemoveMedia={removeMediaOnly}
              onGenerateTTS={handleTTSGeneration}
              onBulkImageUpload={handleBulkImageUpload}
              onAudioUpload={handleAudioUpload}
              onPreviewClick={() => setIsPreviewOpen(true)}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* 최종 영상 미리보기 모달 */}
      <FinalPreview
        sections={sections}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        projectId={projectId}
        title={projectName || title}
        subtitleSettings={subtitleSettings}
        titleSettings={titleSettings}
        videoSettings={videoSettings}
      />
    </div>
  )
}

export default Editor